/**
 * Prix e-commerce : l'hôte (storefront) envoie les prix en CHAÎNES déjà formatées
 * ("8.39€", "8,39 €", "€8.39", "1 234,56 €"…). Pour la saisie libre de quantité on
 * doit recalculer un total (quantité × prix unitaire du palier) puis le réafficher
 * dans le MÊME habillage que les prix reçus. Ces deux fonctions isolent le parsing
 * et le reformatage en préservant devise, position et séparateur décimal.
 */

/** Extrait la valeur numérique d'un prix formaté, quel que soit l'habillage. */
export function parsePriceNumber(raw?: string | number | null): number | null {
    if (raw == null) {
        return null;
    }
    if (typeof raw === 'number') {
        return Number.isFinite(raw) ? raw : null;
    }
    let s = String(raw).replace(/[^0-9.,-]/g, '');
    if (!s) {
        return null;
    }
    const hasDot = s.includes('.');
    const hasComma = s.includes(',');
    if (hasDot && hasComma) {
        // Le séparateur décimal est le dernier rencontré ; l'autre = milliers.
        if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
            s = s.replace(/\./g, '').replace(',', '.');
        } else {
            s = s.replace(/,/g, '');
        }
    } else if (hasComma) {
        s = s.replace(',', '.');
    }
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
}

function escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Reformate `value` en copiant l'habillage de `template` : symbole de devise,
 * sa position (préfixe/suffixe), l'espace éventuel et le séparateur décimal.
 * Sans devise détectable => nombre à 2 décimales avec le séparateur du template.
 */
export function formatPriceLike(template: string | undefined | null, value: number): string {
    const t = (template ?? '').trim();
    const useComma = /,\d{1,2}(?!\d)/.test(t) || (t.includes(',') && !t.includes('.'));
    const symbol = (t.match(/[^\d.,\s-]+/) || [''])[0];
    let num = value.toFixed(2);
    if (useComma) {
        num = num.replace('.', ',');
    }
    if (!symbol) {
        return num;
    }
    const before = t.startsWith(symbol);
    const esc = escapeRegExp(symbol);
    const spaced = new RegExp(before ? `${esc}\\s` : `\\s${esc}`).test(t);
    const sep = spaced ? ' ' : '';
    return before ? `${symbol}${sep}${num}` : `${num}${sep}${symbol}`;
}
